import { UUID } from "@bitory-gmbh/nidavellir";
import { Kind } from "@/services/backend/samples/types/kind";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";

/**
 * Sample is a base interface for all samples. It should not be used directly.
 */
export interface Sample {
  id: string;
  /**
   * The company the sample was taken for. It cannot be changed after the
   * sample was created.
   */
  companyId: UUID;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  number: string;
  kind: Kind;
  location: string;
  spot: string;
  labId: EntityId;
  comment: string;
  createdAt: string | null;
}

export interface BaseSamplePatchRequest {
  id: string;
  number?: string;
  labId?: string;
  comment?: string;
}

/**
 * NewSampleRequest holds what identifies a new sample and where it was
 * taken. Everything else is a default, taken from the company's base data.
 * It is the body of the create endpoints.
 */
export interface NewSampleRequest {
  id: UUID;
  companyId: UUID;
  documentId: UUID;
  documentNamespace: Namespace;
  /** Generated by the client, see {@link generateSampleNumber}. */
  number: string;
  location: string;
  spot: string;
}

/**
 * Builds the {@link Sample} fields of a new sample. Mirrors the backend
 * constructors: the sample is taken now and has no lab or comment yet.
 */
export function newSample(
  id: UUID,
  kind: Kind,
  request: Pick<
    Sample,
    | "companyId"
    | "documentId"
    | "documentNamespace"
    | "number"
    | "location"
    | "spot"
  >,
): Sample {
  return {
    id,
    companyId: request.companyId,
    documentId: request.documentId,
    documentNamespace: request.documentNamespace,
    number: request.number,
    kind,
    location: request.location,
    spot: request.spot,
    labId: null,
    comment: "",
    createdAt: new Date().toISOString(),
  };
}

/**
 * Derives the number of the sample of a set taken with the growth medium,
 * e.g. "2026-9-18.123-CASO". Mirrors `sample.ChildNumber` in the backend.
 */
export function childSampleNumber(
  setNumber: string,
  growthMedium: GrowthMedium,
): string {
  return `${setNumber}-${growthMedium.toUpperCase()}`;
}

export function generateSampleNumber(): string {
  const date = new Date();
  const random = Math.floor(Math.random() * 1_000);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.${random}`;
}

export function updateSampleDocument<T extends Sample>(
  sample: T,
  id: EntityId,
  namespace: Namespace,
): T {
  return {
    ...sample,
    documentId: id,
    documentNamespace: namespace,
  };
}

export function updateSampleNumber<T extends Sample>(
  sample: T,
  number: string,
): T {
  return {
    ...sample,
    number,
  };
}

export function updateSampleLab<T extends Sample>(
  sample: T,
  labId: EntityId,
): T {
  return {
    ...sample,
    labId,
  };
}

export function updateSampleComment<T extends Sample>(
  sample: T,
  comment: string,
): T {
  return {
    ...sample,
    comment,
  };
}

export function updateSampleCreatedAt<T extends Sample>(
  sample: T,
  createdAt: string | null,
): T {
  return {
    ...sample,
    createdAt,
  };
}

export function updateLocation<T extends { location: string }>(
  sample: T,
  location: string,
): T {
  return {
    ...sample,
    location,
  };
}

export function updateSpot<T extends { spot: string }>(
  sample: T,
  spot: string,
): T {
  return {
    ...sample,
    spot,
  };
}

export function updateSampleTakers<T extends { sampleTakers: string }>(
  sample: T,
  sampleTakers: string,
): T {
  return {
    ...sample,
    sampleTakers,
  };
}
